import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Grid
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Reply as ReplyIcon,
  Person as PersonIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import { collection, query, orderBy, getDocs, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { checkPhoneExists, updateCustomer } from '../../services/customerService';

interface ProductSuggestion {
  id: string;
  name: string;
  phone: string;
  address: string;
  suggestion: string;
  link: string;
  productId: string;
  productName: string;
  status: 'new' | 'reviewed' | 'completed';
  adminReply?: string;
  repliedAt?: any;
  createdAt: any;
}

const ProductSuggestionsPage: React.FC = () => {
  const [suggestions, setSuggestions] = useState<ProductSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<ProductSuggestion | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  const fetchSuggestions = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'product_suggestions'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ProductSuggestion[];
      setSuggestions(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching suggestions:', err);
      setError('حدث خطأ أثناء تحميل الاقتراحات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الاقتراح؟')) {
      try {
        await deleteDoc(doc(db, 'product_suggestions', id));
        setSuggestions(suggestions.filter(s => s.id !== id));
        if (selectedSuggestion?.id === id) setSelectedSuggestion(null);
      } catch (err) {
        console.error('Error deleting suggestion:', err);
        alert('حدث خطأ أثناء الحذف');
      }
    }
  };

  const handleStatusChange = async (id: string, newStatus: 'reviewed' | 'completed') => {
    try {
      await updateDoc(doc(db, 'product_suggestions', id), {
        status: newStatus
      });
      setSuggestions(suggestions.map(s => s.id === id ? { ...s, status: newStatus } : s));
      if (selectedSuggestion?.id === id) {
        setSelectedSuggestion({ ...selectedSuggestion, status: newStatus });
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('حدث خطأ أثناء تحديث الحالة');
    }
  };

  const handleSendReply = async () => {
    if (!selectedSuggestion || !replyText.trim()) return;

    try {
      setReplyLoading(true);
      const suggestionRef = doc(db, 'product_suggestions', selectedSuggestion.id);
      
      const updateData = {
        adminReply: replyText,
        repliedAt: serverTimestamp(),
        status: 'reviewed' as const
      };

      await updateDoc(suggestionRef, updateData);

      // محاولة مزامنة الرد مع ملف العميل إذا وجد
      try {
        const customer = await checkPhoneExists(selectedSuggestion.phone);
        if (customer) {
          const newNote = `\n[رد على اقتراح سعر - ${new Date().toLocaleDateString('ar-EG')}]: ${replyText}`;
          await updateCustomer(customer.id, {
            notes: (customer.notes || '') + newNote
          });
        }
      } catch (custErr) {
        console.error('Error updating customer notes with reply:', custErr);
      }

      // تحديث الواجهة
      setSuggestions(suggestions.map(s => 
        s.id === selectedSuggestion.id ? { ...s, ...updateData, repliedAt: new Date() } : s
      ));
      setSelectedSuggestion({ ...selectedSuggestion, ...updateData, repliedAt: new Date() });
      setReplyText('');
      alert('تم إرسال الرد بنجاح وحفظه في ملف العميل');
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('حدث خطأ أثناء إرسال الرد');
    } finally {
      setReplyLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'new':
        return <Chip label="جديد" color="error" size="small" />;
      case 'reviewed':
        return <Chip label="تمت المراجعة" color="info" size="small" />;
      case 'completed':
        return <Chip label="مكتمل" color="success" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">اقتراحات الأسعار من العملاء</Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchSuggestions} variant="outlined">
          تحديث
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>التاريخ</TableCell>
                <TableCell>العميل</TableCell>
                <TableCell>التواصل</TableCell>
                <TableCell>المنتج</TableCell>
                <TableCell>الاقتراح / السعر الأقل</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    لا توجد اقتراحات حالياً
                  </TableCell>
                </TableRow>
              ) : (
                suggestions.map((suggestion) => (
                  <TableRow 
                    key={suggestion.id} 
                    sx={{ 
                      bgcolor: suggestion.status === 'new' ? 'action.hover' : 'inherit',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    onClick={() => {
                        setSelectedSuggestion(suggestion);
                        if (suggestion.status === 'new') handleStatusChange(suggestion.id, 'reviewed');
                    }}
                  >
                    <TableCell>
                      {suggestion.createdAt?.toDate ? suggestion.createdAt.toDate().toLocaleString('ar-EG') : 'غير معروف'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{suggestion.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{suggestion.address}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{suggestion.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      <Link 
                        href={suggestion.link} 
                        target="_blank" 
                        sx={{ display: 'flex', alignItems: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {suggestion.productName} <LaunchIcon sx={{ ml: 0.5, fontSize: 16 }} />
                      </Link>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {suggestion.suggestion}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(suggestion.status)}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="معاينة وتفاصيل">
                            <IconButton onClick={() => setSelectedSuggestion(suggestion)} color="primary">
                              <ViewIcon />
                            </IconButton>
                        </Tooltip>
                        {suggestion.status !== 'completed' && (
                          <Tooltip title="اعتماده كمكتمل">
                            <IconButton onClick={() => handleStatusChange(suggestion.id, 'completed')} color="success">
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="حذف">
                          <IconButton onClick={() => handleDelete(suggestion.id)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* نافذة معاينة الطلب والرد */}
      <Dialog 
        open={!!selectedSuggestion} 
        onClose={() => setSelectedSuggestion(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedSuggestion && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">تفاصيل طلب العميل: {selectedSuggestion.name}</Typography>
                </Box>
                {getStatusChip(selectedSuggestion.status)}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="primary">بيانات العميل</Typography>
                        <Typography variant="body1"><strong>الاسم:</strong> {selectedSuggestion.name}</Typography>
                        <Typography variant="body1"><strong>رقم الهاتف:</strong> {selectedSuggestion.phone}</Typography>
                        <Typography variant="body1"><strong>العنوان:</strong> {selectedSuggestion.address}</Typography>
                    </Grid>
                    <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 2 }} />
                    <Grid item xs={12} md={5.5}>
                        <Typography variant="subtitle2" color="primary">المنتج المقترح</Typography>
                        <Typography variant="body1"><strong>اسم المنتج:</strong> {selectedSuggestion.productName}</Typography>
                        <Link href={selectedSuggestion.link} target="_blank" sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                            فتح صفحة المنتج <LaunchIcon sx={{ ml: 0.5, fontSize: 16 }} />
                        </Link>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: 'action.hover', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                            <Typography variant="subtitle2" gutterBottom>محتوى الاقتراح:</Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {selectedSuggestion.suggestion}
                            </Typography>
                        </Paper>
                    </Grid>

                    {/* سجل الردود */}
                    {selectedSuggestion.adminReply && (
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                                    <StoreIcon sx={{ mr: 0.5, fontSize: 18 }} /> رد الإدارة:
                                </Typography>
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {selectedSuggestion.adminReply}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'left' }}>
                                    تم الرد في: {selectedSuggestion.repliedAt?.toDate ? selectedSuggestion.repliedAt.toDate().toLocaleString('ar-EG') : 'الآن'}
                                </Typography>
                            </Paper>
                        </Grid>
                    )}

                    {/* نموذج الرد */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>إرسال رد للعميل:</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="اكتب ردك هنا... سيتم حفظ الرد في ملف العميل أيضاً"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            disabled={replyLoading}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSelectedSuggestion(null)}>إغلاق</Button>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={replyLoading ? <CircularProgress size={20} color="inherit" /> : <ReplyIcon />}
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || replyLoading}
                >
                    إرسال الرد
                </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProductSuggestionsPage;

